/**
 * `/admin/organizations/$orgId` — Org admin detail page (issue #275 —
 * multi-tenancy Phase 4; invite affordances added in #276 — Phase 5).
 *
 * Sections:
 *   1. Heading + breadcrumb-style "Organization" label.
 *   2. Details card — editable name + slug + Save button.
 *   3. Members table — per-row Promote / Demote / Remove.  Owner-protection
 *      lives server-side; the SPA surfaces 422 `LAST_OWNER_PROTECTED` as a
 *      clear toast per the issue spec.
 *   4. Invite-by-email form (Phase 5).  POST returns one of:
 *        - `kind: "existing-user"` → toast "{email} joined", refresh members.
 *        - `kind: "invitation"`    → open a dialog showing the inviteUrl with
 *                                     a copy-to-clipboard button.
 *   5. Pending invitations table (Phase 5) — Email / Invited By / Invited At
 *      / Expires / Actions (Copy URL + Revoke).
 *
 * Confirmation:
 *   Uses the shared `useConfirmDialog` hook (issue #255 AC2) — the
 *   previous note about its absence applied only to Phase 4 before the
 *   helper landed. One dialog instance is mounted at the top of the page
 *   and services every destructive prompt (member remove, demote self,
 *   revoke invitation).
 *
 * testIDs (per issue spec):
 *   - `org-detail-page`, `org-detail-name-input`, `org-detail-slug-input`,
 *     `org-detail-save`
 *   - `org-member-row-<userId>`, `org-member-promote-<userId>`,
 *     `org-member-demote-<userId>`, `org-member-remove-<userId>`
 *   - `org-invite-form`, `org-invite-email-input`, `org-invite-submit`
 *   - `org-invite-url-dialog`, `org-invite-url-input`, `org-invite-url-copy`,
 *     `org-invite-url-close`
 *   - `org-invitation-row-<id>`, `org-invitation-copy-<id>`,
 *     `org-invitation-revoke-<id>`
 */

import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SaveIcon from "@mui/icons-material/Save";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useParams } from "@tanstack/react-router";
import type {
  OrgInvitationResponse,
  OrgMemberResponse,
  OrgMemberRoleLiteral,
} from "@ottopilot/shared";
import {
  useCreateOrgInvite,
  useOrgInvitations,
  useOrgMembers,
  useRemoveOrgMember,
  useRevokeOrgInvitation,
  useUpdateOrganization,
  useUpdateOrgMemberRole,
} from "../hooks/useOrgs";
import { useMe } from "../hooks/useAuth";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";

/**
 * Resolve the active user's id from the `me` cache. Used to highlight
 * the actor's own row in the members table + customise the destructive-
 * action confirm copy when they're operating on themselves.
 */
function useActorUserId(): string | undefined {
  const me = useMe();
  return me.data?.user.id;
}

export function OrganizationDetailPage(): ReactElement {
  const { orgId } = useParams({ from: "/authenticated/admin/organizations/$orgId" });
  const actorId = useActorUserId();
  const { data, isPending, isError, error } = useOrgMembers(orgId);
  const invitations = useOrgInvitations(orgId);
  const updateOrg = useUpdateOrganization(orgId);
  const updateRole = useUpdateOrgMemberRole(orgId);
  const removeMember = useRemoveOrgMember(orgId);
  const createInvite = useCreateOrgInvite(orgId);
  const revokeInvitation = useRevokeOrgInvitation(orgId);
  const toast = useToast();
  // Issue #255 AC2 — single MUI confirm dialog instance services the
  // demote-self, member-remove, and revoke-invitation prompts.
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  // Invite form state — kept local so a re-render of the parent (e.g.
  // members refetch) doesn't reset the in-flight field. Cleared on
  // every successful submit (either branch).
  const [inviteEmail, setInviteEmail] = useState("");
  // After a `kind: "invitation"` response, surface the URL in a modal
  // dialog with a copy-to-clipboard affordance. Null = closed.
  const [shownInviteUrl, setShownInviteUrl] = useState<{
    url: string;
    email: string;
    orgName: string;
  } | null>(null);

  // Local form state for the rename surface. Initialised from the
  // server response and re-synced via `useEffect` so a refetch (e.g.
  // after a different operator renamed the org) doesn't lose the
  // operator's in-flight edits unless they hit Save.
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  useEffect(() => {
    if (data) {
      setName(data.organization.name);
      setSlug(data.organization.slug);
    }
  }, [data]);

  const ownerCount = useMemo(() => {
    if (!data) return 0;
    return data.members.filter((m) => m.role === "OWNER").length;
  }, [data]);

  function handleSaveOrg() {
    if (!data) return;
    const changed: { name?: string; slug?: string } = {};
    if (name.trim() !== data.organization.name) changed.name = name.trim();
    if (slug.trim() !== data.organization.slug) changed.slug = slug.trim();
    if (changed.name === undefined && changed.slug === undefined) {
      toast.show({ message: "No changes to save.", severity: "info" });
      return;
    }
    updateOrg.mutate(changed, {
      onSuccess: (res) => {
        toast.showSuccess(`Saved “${res.name}”.`);
      },
      onError: (err) => {
        if (err.code === "SLUG_CONFLICT") {
          toast.showError("Slug is taken by another organization.");
          return;
        }
        toast.showError(err.message || "Could not save changes.");
      },
    });
  }

  async function handleRoleChange(member: OrgMemberResponse, newRole: OrgMemberRoleLiteral) {
    const isSelf = member.userId === actorId;
    if (newRole === "MEMBER" && isSelf) {
      const ok = await confirm({
        title: "Demote yourself from OWNER to MEMBER?",
        body: "Demote yourself from OWNER to MEMBER? You will lose owner-only abilities until another OWNER promotes you back.",
        confirmLabel: "Demote",
        confirmColor: "warning",
      });
      if (!ok) return;
    }
    updateRole.mutate(
      { userId: member.userId, role: newRole },
      {
        onSuccess: () => {
          toast.showSuccess(
            newRole === "OWNER"
              ? `Promoted ${member.email} to OWNER.`
              : `Demoted ${member.email} to MEMBER.`,
          );
        },
        onError: (err) => {
          if (err.code === "LAST_OWNER_PROTECTED") {
            toast.showError("Cannot remove the last OWNER. Promote another member first.");
            return;
          }
          toast.showError(err.message || "Could not update role.");
        },
      },
    );
  }

  /**
   * Best-effort copy-to-clipboard.  The Clipboard API requires a secure
   * context; falls back to a transient text-selection on failure so the
   * operator can manually Cmd+C.
   */
  async function copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through to selection fallback
    }
    return false;
  }

  function handleInviteSubmit(e: React.FormEvent) {
    e.preventDefault();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      toast.show({ message: "Enter an email address.", severity: "info" });
      return;
    }
    createInvite.mutate(
      { email },
      {
        onSuccess: (res) => {
          if (res.kind === "existing-user") {
            toast.showSuccess(`${res.membership.email} joined.`);
            setInviteEmail("");
            return;
          }
          // kind === "invitation"
          setShownInviteUrl({
            url: res.invitation.inviteUrl,
            email: res.invitation.email,
            orgName: data?.organization.name ?? "the organization",
          });
          setInviteEmail("");
        },
        onError: (err) => {
          if (err.code === "ALREADY_MEMBER") {
            toast.showError("This user is already a member of the organization.");
            return;
          }
          if (err.code === "ALREADY_INVITED") {
            toast.showError("A pending invitation for this email already exists.");
            return;
          }
          if (err.code === "SIGNUP_DOMAIN_NOT_ALLOWED") {
            toast.showError("This email's domain is not permitted to sign up.");
            return;
          }
          toast.showError(err.message || "Could not send invitation.");
        },
      },
    );
  }

  function handleCopyInviteUrl(url: string, ctx: "dialog" | "list", id?: string) {
    void copyToClipboard(url).then((ok) => {
      if (ok) {
        toast.showSuccess("Invite link copied to clipboard.");
      } else {
        toast.show({
          message: "Could not copy automatically — select the link and copy manually.",
          severity: "info",
        });
      }
      // Mark these so the test harness can assert against the captured args.
      void ctx;
      void id;
    });
  }

  async function handleRevokeInvitation(inv: OrgInvitationResponse) {
    const ok = await confirm({
      title: `Revoke invitation for ${inv.email}?`,
      body: `Revoke the pending invitation for ${inv.email}? They will no longer be able to use the invite link.`,
      confirmLabel: "Revoke",
    });
    if (!ok) return;
    revokeInvitation.mutate(
      { invitationId: inv.id },
      {
        onSuccess: () => {
          toast.showSuccess(`Revoked invitation for ${inv.email}.`);
        },
        onError: (err) => {
          toast.showError(err.message || "Could not revoke invitation.");
        },
      },
    );
  }

  async function handleRemove(member: OrgMemberResponse) {
    const isSelf = member.userId === actorId;
    const ok = await confirm({
      title: isSelf ? "Remove yourself from this organization?" : `Remove ${member.email}?`,
      body: isSelf
        ? "Remove yourself from this organization? You will lose access immediately."
        : `Remove ${member.email} from this organization?`,
      confirmLabel: "Remove",
    });
    if (!ok) return;
    removeMember.mutate(
      { userId: member.userId },
      {
        onSuccess: () => {
          toast.showSuccess(isSelf ? "You left the organization." : `Removed ${member.email}.`);
        },
        onError: (err) => {
          if (err.code === "LAST_OWNER_PROTECTED") {
            toast.showError("Cannot remove the last OWNER. Promote another member first.");
            return;
          }
          toast.showError(err.message || "Could not remove member.");
        },
      },
    );
  }

  if (isPending) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", py: 6 }}
        data-testid="org-detail-page-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Alert severity="error" data-testid="org-detail-page-error">
        {error?.message ?? "Could not load organization."}
      </Alert>
    );
  }

  const dirty = name.trim() !== data.organization.name || slug.trim() !== data.organization.slug;

  return (
    <Stack spacing={3} data-testid="org-detail-page">
      {confirmDialog}
      <Box>
        <Typography variant="overline" color="text.secondary">
          Organization
        </Typography>
        <Typography variant="h2" data-testid="org-detail-heading">
          {data.organization.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          /{data.organization.slug}
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3 }} data-testid="org-detail-rename-card">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Details
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={updateOrg.isPending}
            inputProps={{ "data-testid": "org-detail-name-input", maxLength: 120 }}
          />
          <TextField
            label="Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            fullWidth
            disabled={updateOrg.isPending}
            helperText="Lowercase letters, digits, and hyphens. Must be unique across all organizations."
            inputProps={{ "data-testid": "org-detail-slug-input", maxLength: 120 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveOrg}
              disabled={!dirty || updateOrg.isPending}
              data-testid="org-detail-save"
            >
              Save
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" data-testid="org-detail-members-card">
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography variant="h5">Members</Typography>
          <Typography variant="body2" color="text.secondary">
            {data.members.length} member{data.members.length === 1 ? "" : "s"} · {ownerCount} owner
            {ownerCount === 1 ? "" : "s"}
          </Typography>
        </Box>
        <TableContainer>
          <Table aria-label="Organization members" data-testid="org-detail-members-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Joined</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.members.map((member) => {
                const isSelf = member.userId === actorId;
                const isOwner = member.role === "OWNER";
                // Server-side owner-protection still runs; this just
                // disables the buttons that would obviously fail.
                const removeWouldStrand = isOwner && ownerCount <= 1;
                const demoteWouldStrand = isOwner && ownerCount <= 1;
                return (
                  <TableRow
                    key={member.userId}
                    data-testid={`org-member-row-${member.userId}`}
                    selected={isSelf}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {member.name ?? "—"}
                        {isSelf ? (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, color: "text.secondary" }}
                          >
                            (you)
                          </Typography>
                        ) : null}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {member.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={member.role}
                        color={isOwner ? "primary" : "default"}
                        data-testid={`org-member-role-${member.userId}`}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(member.joinedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {isOwner ? (
                          <Button
                            size="small"
                            startIcon={<TrendingDownIcon />}
                            onClick={() => {
                              void handleRoleChange(member, "MEMBER");
                            }}
                            disabled={updateRole.isPending || demoteWouldStrand}
                            data-testid={`org-member-demote-${member.userId}`}
                          >
                            Demote
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<TrendingUpIcon />}
                            onClick={() => {
                              void handleRoleChange(member, "OWNER");
                            }}
                            disabled={updateRole.isPending}
                            data-testid={`org-member-promote-${member.userId}`}
                          >
                            Promote
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => {
                            void handleRemove(member);
                          }}
                          disabled={removeMember.isPending || removeWouldStrand}
                          data-testid={`org-member-remove-${member.userId}`}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }} data-testid="org-invite-form">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Invite a member
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Existing accounts are added directly. New emails get an invite link to share — sign-up
          completes the join.
        </Typography>
        <Box
          component="form"
          onSubmit={handleInviteSubmit}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "flex-start" },
          }}
        >
          <TextField
            label="Email"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            disabled={createInvite.isPending}
            fullWidth
            inputProps={{ "data-testid": "org-invite-email-input", maxLength: 254 }}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={<PersonAddIcon />}
            disabled={createInvite.isPending || inviteEmail.trim().length === 0}
            data-testid="org-invite-submit"
            sx={{ alignSelf: { xs: "flex-end", sm: "stretch" }, whiteSpace: "nowrap" }}
          >
            {createInvite.isPending ? "Sending…" : "Send invite"}
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" data-testid="org-invitations-card">
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography variant="h5">Pending invitations</Typography>
          <Typography variant="body2" color="text.secondary">
            {invitations.data?.invitations.length ?? 0} pending
          </Typography>
        </Box>
        {invitations.isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : invitations.isError ? (
          <Alert severity="error" sx={{ mx: 3, mb: 3 }} data-testid="org-invitations-error">
            {invitations.error?.message ?? "Could not load invitations."}
          </Alert>
        ) : invitations.data && invitations.data.invitations.length === 0 ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No pending invitations.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table aria-label="Pending invitations" data-testid="org-invitations-table">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Invited by
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                    Invited at
                  </TableCell>
                  <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.data.invitations.map((inv) => (
                  <TableRow key={inv.id} data-testid={`org-invitation-row-${inv.id}`}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {inv.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      <Typography variant="body2">
                        {inv.invitedByEmail ?? inv.invitedByName ?? "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(inv.invitedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(inv.expiresAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => handleCopyInviteUrl(inv.inviteUrl, "list", inv.id)}
                          data-testid={`org-invitation-copy-${inv.id}`}
                        >
                          Copy link
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => {
                            void handleRevokeInvitation(inv);
                          }}
                          disabled={revokeInvitation.isPending}
                          data-testid={`org-invitation-revoke-${inv.id}`}
                        >
                          Revoke
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={shownInviteUrl !== null}
        onClose={() => setShownInviteUrl(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="invite-url-dialog-title"
        data-testid="org-invite-url-dialog"
      >
        <DialogTitle id="invite-url-dialog-title">Invitation link ready</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {shownInviteUrl
              ? `Send this link to ${shownInviteUrl.email} to let them sign up and join ${shownInviteUrl.orgName}.`
              : ""}
          </DialogContentText>
          <TextField
            fullWidth
            value={shownInviteUrl?.url ?? ""}
            inputProps={{
              readOnly: true,
              "data-testid": "org-invite-url-input",
              "aria-label": "Invite URL",
            }}
            onFocus={(e) => e.target.select()}
            sx={{ fontFamily: "monospace" }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (shownInviteUrl) handleCopyInviteUrl(shownInviteUrl.url, "dialog");
            }}
            startIcon={<ContentCopyIcon />}
            variant="contained"
            data-testid="org-invite-url-copy"
          >
            Copy link
          </Button>
          <Button onClick={() => setShownInviteUrl(null)} data-testid="org-invite-url-close">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
